import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
// import { LiveCallsCard } from './components/LiveCallsCard';
import { AverageHandlingTimeChart } from './components/AverageHandlingTimeChart';
import { VolumeTrendsChart } from './components/VolumeTrendsChart';
import { CallEndingsChart } from './components/CallEndingsChart';
import { SearchableDropdown } from '@/components/ui/searchable-dropdown';
import { CalendarDatePicker } from '@/components/calendar-date-picker';
import { AssistantSelector } from './components/AssistantSelector';
import { SiteHeader } from '@/components/site-header';
import { PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { containerVariants } from '@/components/animations';
import { DateRange } from 'react-day-picker';
import { getRubrics } from '@/api/rubrics/api';
import type { Rubric } from '@/api/rubrics/types';

const RUBRIC_TYPE_CONFIG = {
    inboundPhoneCall: {
        label: "Inbound Calls",
        icon: PhoneIncoming,
        color: "text-green-500",
        bgColor: "bg-green-50/50 dark:bg-green-500/10"
    },
    outboundPhoneCall: {
        label: "Outbound Calls",
        icon: PhoneOutgoing,
        color: "text-blue-500",
        bgColor: "bg-blue-50/50 dark:bg-blue-500/10"
    }
} as const;

// Helper function to format date in local timezone
const formatDateForAPI = (date: Date | undefined): string | undefined => {
    if (!date) return undefined;
    
    // Get the local date components to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
};

const Analytics = () => {    
    const [selectedRubric, setSelectedRubric] = useState<string | undefined>(undefined);
    const [selectedKeyPath, setSelectedKeyPath] = useState<string | null>(null);
    const [date, setDate] = useState<DateRange>({
        from: undefined,
        to: undefined,
    });
    const [rubrics, setRubrics] = useState<Rubric[]>([]);
    const [isLoadingRubrics, setIsLoadingRubrics] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [selectedAssistant, setSelectedAssistant] = useState<string | undefined>(undefined);
    const [granularity, setGranularity] = useState<'day' | 'week' | 'month' | 'year'>('day');

    useEffect(() => {
        const fetchRubrics = async () => {
            setIsLoadingRubrics(true);
            try {
                const response = await getRubrics();
                if (response.data) {
                    setRubrics(response.data.data.rubrics);
                    setError(null);
                } else if (response.error) {
                    throw new Error(response.error)
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to fetch rubrics';
                setError(message);
                console.error(err);
            } finally {
                setIsLoadingRubrics(false);
            }
        };

        fetchRubrics();
        
        // No cleanup needed - state will be discarded on unmount anyway
    }, []);

    const handleDateSelect = useCallback((range: { from: Date | undefined; to: Date | undefined }) => {
        setDate(range);
    }, []);

    const handleAssistantSelect = useCallback((assistantId: string | undefined) => {
        setSelectedAssistant(assistantId);
    }, []);

    const handleGranularityChange = useCallback((newGranularity: 'day' | 'week' | 'month' | 'year') => {
        setGranularity(newGranularity);
    }, []);

    const handleChartError = useCallback((chartName: string) => (errorMessage: string) => {
        console.error(`Error loading ${chartName}:`, errorMessage);
        setError(`Failed to load data for ${chartName}. Please try again later.`);
    }, []);

    const getKeyPaths = (rubric: Rubric | undefined) => {
        if (!rubric) return [];
        const keypaths: { id: string; name: string }[] = [];
        if (rubric.successEvaluation) keypaths.push({ id: 'successEvaluation', name: 'Success Evaluation' });
        Object.entries(rubric).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'key' in value[0] && 'value' in value[0]) {
                keypaths.push({ id: key, name: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1') });
            }
        });
        return keypaths;
    };

    const dropdownOptions = useMemo(() => {
        const groupedRubrics = rubrics.reduce((acc, rubric) => {
            const type = rubric.type as keyof typeof RUBRIC_TYPE_CONFIG;
            if (!acc[type]) acc[type] = [];
            acc[type].push(rubric);
            return acc;
        }, {} as Record<keyof typeof RUBRIC_TYPE_CONFIG, Rubric[]>);
        return Object.entries(groupedRubrics).map(([type, typeRubrics]) => {
            const typeConfig = RUBRIC_TYPE_CONFIG[type as keyof typeof RUBRIC_TYPE_CONFIG];
            return {
                id: type, label: typeConfig.label, type: type, typeIcon: typeConfig.icon,
                typeColor: typeConfig.color, typeBgColor: typeConfig.bgColor,
                subOptions: typeRubrics.map(rubric => ({
                    id: rubric._id, label: rubric.name,
                    subOptions: getKeyPaths(rubric).map(keypath => ({ id: keypath.id, label: keypath.name }))
                }))
            };
        });
    }, [rubrics]);

    // Memoize commonProps to prevent unnecessary re-renders of all charts
    // when only granularity changes (which only affects Volume Trends)
    const commonProps = useMemo(() => ({
        startDate: formatDateForAPI(date.from),
        endDate: formatDateForAPI(date.to),
        rubricId: selectedRubric,
        keyPath: selectedKeyPath || undefined,
        assistantId: selectedAssistant
    }), [date.from, date.to, selectedRubric, selectedKeyPath, selectedAssistant]);

    const renderAnalyticsDashboard = () => {


        return (
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid gap-6"
            >
                {error && (
                    <div 
                        className="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400 border border-red-300 dark:border-red-800"
                        role="alert"
                    >
                        <span className="font-medium">Error!</span> {error}
                    </div>
                )}
                <VolumeTrendsChart
                    {...commonProps}
                    granularity={granularity}
                    onGranularityChange={handleGranularityChange}
                    onError={handleChartError('Volume Trends')}
                />
                <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
                    {/* <LiveCallsCard /> */}
                    <AverageHandlingTimeChart {...commonProps} onError={handleChartError('Avg. Handling Time')} />
                    <CallEndingsChart {...commonProps} onError={handleChartError('Call Endings')} />
                </div>
            </motion.div>
        );
    };

    return (
        <>
            <SiteHeader 
                title="Analytics"
                subtitle="Monitor call performance and trends in real-time."
            >
                <AssistantSelector 
                    selectedAssistantId={selectedAssistant}
                    onAssistantSelect={handleAssistantSelect} 
                />
                <SearchableDropdown
                    options={dropdownOptions}
                    value={selectedRubric}
                    subValue={selectedKeyPath || undefined}
                    onSelect={(rubricId, keyPath) => {
                        setSelectedRubric(rubricId);
                        setSelectedKeyPath(keyPath || null);
                    }}
                    loading={isLoadingRubrics}
                    placeholder="Select Rubric..."
                    searchPlaceholder="Search rubrics..."
                    emptyText="No rubric found."
                    width="250px"
                />
                <CalendarDatePicker
                    date={date}
                    onDateSelect={handleDateSelect}
                    numberOfMonths={2}
                    closeOnSelect={true}
                />
            </SiteHeader>
            <div className="flex-1 overflow-auto">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-4 py-6 md:px-8 md:py-8"
                >
                    {renderAnalyticsDashboard()}
                </motion.div>
            </div>
        </>
    );
};

export default Analytics;
